/**
 * Helpers for the server-rendered top-level loading spinner
 * (#backend-login-spinner, partials/_portal_loading.html.erb).
 *
 * Enforces a minimum-visible window so fast loads don't flash the spinner
 * for a few frames and disappear. State (shownAt timestamp) lives on the
 * DOM element's dataset so it survives across separate Vite entrypoint
 * bundles (the *_login.ts and *_core.ts entrypoints each import this module
 * but don't share module instances).
 *
 * The spinner is reusable after boot: logout re-shows it as a cover for the
 * /logout round trip and the reload that follows. So retiring it drops the id
 * and hides the node rather than removing it — the node stays available to
 * showInitialLoadingSpinner, while dashboarder.ts's getElementById gate still
 * reads it as gone.
 */

declare global {
  interface Window {
    // Published by the inline boot watchdog in partials/_portal_loading.html.erb.
    cancelPortalBootWatchdog?: () => void;
  }
}

import {
  attachSplashParticleField,
  detachSplashParticleField,
} from '@/lib/loginParticleField';

const SPINNER_ID = 'backend-login-spinner';
/**
 * Stamped on <html> the first time the cover drops. The sign-in screens mount
 * behind it, so this is the moment their entrance can actually be seen —
 * app/assets/stylesheets/application.tailwind.css gates the animation on it.
 */
const REVEALED_ATTR = 'data-portal-revealed';
const RETIRED_ATTR = 'data-retired-spinner';
const LABEL_SELECTOR = '[data-spinner-label]';
const MIN_VISIBLE_MS = 1000;

// The partial's tw-z-50 is enough at boot, where the portal is still
// display:none behind it. Re-showing over a live portal has to clear
// everything already on screen: .top-progress-bar (fixed, 110), Bootstrap
// modals (1050+), the alerter (10000000), .pac-container (1000000000).
const COVER_Z_INDEX = '2000000000';

function getSpinner(): HTMLElement | null {
  return document.getElementById(SPINNER_ID);
}

function getRetiredSpinner(): HTMLElement | null {
  return document.querySelector<HTMLElement>(`[${RETIRED_ATTR}]`);
}

// shownAt is stamped server-side via the inline script in _portal_loading.html.erb
// at first paint. Use Date.now() (not performance.now()) so vi.useFakeTimers()
// can drive timing in tests; for a 1s UX gate the wall-clock vs monotonic
// difference is immaterial.
function getElapsedMs(spinner: HTMLElement): number {
  const shownAt = spinner.dataset.shownAt;
  if (!shownAt) return Number.POSITIVE_INFINITY;
  return Date.now() - Number(shownAt);
}

// Called the moment a hide/retire is requested, not when it runs: boot has
// already succeeded by then, so the watchdog can neither mistake the logout
// re-show for a wedge nor leave its attempts guard set for the next one.
function cancelBootWatchdog(): void {
  window.cancelPortalBootWatchdog?.();
}

function markRevealed(): void {
  document.documentElement.setAttribute(REVEALED_ATTR, '');
}

// Every re-cover today is a logout that navigates immediately, so nothing
// mounts behind a stale stamp. Cleared anyway so the entrance stays correct by
// construction: the invariant is "the drop reveals", not "re-covers happen to
// unload the document".
function clearRevealed(): void {
  document.documentElement.removeAttribute(REVEALED_ATTR);
}

export function hideInitialLoadingSpinner(): void {
  cancelBootWatchdog();
  afterMinSpinnerTime((spinner) => {
    spinner.style.display = 'none';
    markRevealed();
    // The sign-in card's own canvas keeps the dot field going; this one would
    // just hold the frame loop open painting nothing.
    detachSplashParticleField();
  });
}

/**
 * Take the spinner off screen for good, as far as the booting portal is
 * concerned. Keeps the node around so logout can bring it back.
 */
export function retireInitialLoadingSpinner(): void {
  cancelBootWatchdog();
  afterMinSpinnerTime((spinner) => {
    spinner.style.display = 'none';
    spinner.removeAttribute('id');
    spinner.setAttribute(RETIRED_ATTR, '');
    markRevealed();
    detachSplashParticleField();
  });
}

/**
 * Cover the viewport with the branded spinner again, optionally relabeled
 * ("Logging out"). Returns false when the page never rendered one, so callers
 * on the legacy Sprockets pages carry on without visual feedback.
 */
export function showInitialLoadingSpinner(label?: string): boolean {
  const spinner = getSpinner() ?? getRetiredSpinner();
  if (!spinner) return false;

  spinner.id = SPINNER_ID;
  spinner.removeAttribute(RETIRED_ATTR);

  if (label) {
    const labelElement = spinner.querySelector(LABEL_SELECTOR);
    if (labelElement) labelElement.textContent = label;
  }

  // A hide/retire still inside its min-visible window would otherwise fire
  // later and yank this cover back off screen.
  cancelPendingSpinnerTimer(spinner);

  spinner.dataset.shownAt = String(Date.now());
  spinner.style.zIndex = COVER_Z_INDEX;
  spinner.style.display = '';
  clearRevealed();
  // Back on screen over a live portal, so the field has to start again.
  attachSplashParticleField();
  return true;
}

function cancelPendingSpinnerTimer(spinner: HTMLElement): void {
  const pending = spinner.dataset.pendingTimer;
  if (!pending) return;
  window.clearTimeout(Number(pending));
  delete spinner.dataset.pendingTimer;
}

function afterMinSpinnerTime(callback: (spinner: HTMLElement) => void): void {
  const spinner = getSpinner();
  if (!spinner) return;

  const remaining = Math.max(0, MIN_VISIBLE_MS - getElapsedMs(spinner));
  if (remaining === 0) {
    callback(spinner);
    return;
  }

  // The handle lives on the dataset (like shownAt) so a re-show from another
  // entrypoint bundle can still cancel this.
  cancelPendingSpinnerTimer(spinner);
  const timer = window.setTimeout(() => {
    const current = getSpinner();
    if (!current || current.dataset.pendingTimer !== String(timer)) return;
    delete current.dataset.pendingTimer;
    callback(current);
  }, remaining);
  spinner.dataset.pendingTimer = String(timer);
}
